import { useLocalisation } from '@archipro-website/localisation';
import { cn } from '@rocco/utils/cn';
import { getFormattedLocalisedDate } from '@modules/date-time/util/get-localised-date';

/**
 * The article's publish date, formatted for the visitor's market.
 *
 * Deliberately not `utcDateToLocalTimezone`: that resolves through
 * `dayjs.tz.guess()`, which answers with the server's zone during SSR and the
 * reader's in the browser — a hydration text mismatch, and a calendar date that
 * can land a day out. `localiseDate` follows the market, so both renders agree.
 * Label and `datetime` come from the same source, so the date a reader sees is
 * the one a crawler parses.
 */
export const useRevampPublishDate = (publishDate?: string) => {
    const { localiseDate } = useLocalisation();

    if (!publishDate) return undefined;

    const label = getFormattedLocalisedDate(
        localiseDate,
        publishDate,
        'D MMMM YYYY',
    );
    const iso = getFormattedLocalisedDate(
        localiseDate,
        publishDate,
        'YYYY-MM-DD',
    );

    return label && iso ? { label, iso } : undefined;
};

/**
 * The publish date as a real `<time>`. The JSON-LD carries `datePublished`
 * either way, but Google's article guidance asks for a visible date too, and
 * the standard and flexi title sections showed none.
 *
 * Renders nothing when the article has no publish date, so callers can drop it
 * into a byline row unconditionally.
 */
export const RevampPublishDateLine = ({
    publishDate,
    className,
}: {
    /** Raw publish date from the loader; formatted here, not by the caller. */
    publishDate?: string;
    /** Type styles, so each title section can match its own byline row. */
    className?: string;
}) => {
    const date = useRevampPublishDate(publishDate);

    if (!date) return null;

    return (
        <time className={cn('m-0', className)} dateTime={date.iso}>
            {date.label}
        </time>
    );
};
